import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { AvailabilityBadge, FreshnessBadge } from "../components/StatusBadge";
import { ArrowLeft, Phone, Globe, Mail, MapPin, Clock, Bus, ShieldAlert } from "lucide-react";

const DISCLAIMER =
  "Availability reflects the provider's most recent report and does not guarantee admission. Clinical eligibility, program appropriateness, funding, insurance coverage, and transportation arrangements must be confirmed directly with the provider. Do not enter or transmit patient information through this application.";

function Field({ label, children }) {
  return (
    <div>
      <div className="text-xs font-bold uppercase tracking-widest text-slate-400">{label}</div>
      <div className="mt-0.5 text-sm font-medium text-slate-800">{children || "—"}</div>
    </div>
  );
}

export default function ProgramDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/programs/${id}`).then((r) => setP(r.data)).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="p-8 text-slate-500">Loading…</div>;
  if (!p) return <div className="p-8 text-slate-500">Program not found.</div>;

  const openPayers = (p.payers || []).filter((x) => x.contracted && x.accepting);
  const contractedOnly = (p.payers || []).filter((x) => x.contracted && !x.accepting);

  return (
    <div className="mx-auto max-w-4xl">
      <Link to="/search" className="mb-4 inline-flex items-center gap-1 text-sm font-semibold text-teal-700 hover:underline">
        <ArrowLeft size={15} /> Back to directory
      </Link>

      {p.photo_url && (
        <img src={p.photo_url} alt={p.facility_name} className="mb-4 h-52 w-full rounded-2xl border border-slate-200 object-cover" />
      )}

      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-slate-400">{p.org_name}</div>
            <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900">{p.facility_name}</h1>
            <div className="mt-1 text-base text-slate-600">{p.name}</div>
            <div className="mt-1 text-sm font-semibold text-teal-700">ASAM {p.asam_level_code} · {p.asam_official_name}</div>
          </div>
          <div className="text-right">
            <div className={`font-display text-6xl font-black leading-none ${p.available_beds > 0 && p.freshness !== "stale" ? "status-available" : "text-slate-400"}`}>
              {p.available_beds}
            </div>
            <div className="text-xs font-semibold text-slate-500">beds available now</div>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <AvailabilityBadge beds={p.available_beds} freshness={p.freshness} />
          <FreshnessBadge status={p.freshness} />
          <span className="rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-xs font-medium capitalize text-slate-600">{p.population}</span>
          <span className="rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-xs font-medium capitalize text-slate-600">{p.sex_designation}</span>
        </div>

        {p.intake_phone && (
          <a
            href={`tel:${p.intake_phone}`}
            data-testid="call-intake"
            className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-teal-700 py-3.5 text-base font-bold text-white hover:bg-teal-800 sm:hidden"
          >
            <Phone size={18} /> Call intake: {p.intake_phone}
          </a>
        )}
      </div>

      {/* Capacity */}
      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          ["Licensed beds", p.licensed_beds],
          ["Beds offline", p.beds_offline],
          ["Available now", p.available_beds],
          ["Pending admissions", p.pending_admissions],
        ].map(([l, v]) => (
          <div key={l} className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="font-display text-3xl font-extrabold text-slate-900">{v}</div>
            <div className="text-xs font-semibold text-slate-500">{l}</div>
          </div>
        ))}
      </div>

      {p.expected_openings?.length > 0 && (
        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4">
          <div className="text-xs font-bold uppercase tracking-widest text-amber-700">Expected openings</div>
          <ul className="mt-1 text-sm font-medium text-amber-900">
            {p.expected_openings.map((e, i) => (
              <li key={i}>{e.beds} bed(s){e.date ? ` by ${new Date(e.date).toLocaleDateString()}` : ""}{e.unit ? ` — ${e.unit}` : ""}</li>
            ))}
          </ul>
        </div>
      )}

      {p.temporary_limitations?.length > 0 && (
        <div className="mt-4 flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
          <ShieldAlert size={18} className="mt-0.5 shrink-0" />
          <div><strong>Temporary admission limitations:</strong> {p.temporary_limitations.join("; ")}</div>
        </div>
      )}

      {/* Admission + contact */}
      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <h3 className="font-display text-lg font-bold text-slate-900">Admission</h3>
          <div className="mt-3 grid grid-cols-1 gap-3">
            <Field label="Admission hours">{p.admission_hours}</Field>
            <Field label="Weekend & after-hours process">{p.weekend_process}</Field>
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <h3 className="font-display text-lg font-bold text-slate-900">Contact</h3>
          <div className="mt-3 space-y-2 text-sm">
            {p.intake_phone && <div className="flex items-center gap-2"><Phone size={15} className="text-teal-700" /> Intake: <a className="font-semibold text-teal-700" href={`tel:${p.intake_phone}`}>{p.intake_phone}</a></div>}
            {p.after_hours_phone && <div className="flex items-center gap-2"><Clock size={15} className="text-teal-700" /> After-hours: {p.after_hours_phone}</div>}
            {p.email && <div className="flex items-center gap-2"><Mail size={15} className="text-teal-700" /> {p.email}</div>}
            {p.website && <div className="flex items-center gap-2"><Globe size={15} className="text-teal-700" /> <a className="text-teal-700 hover:underline" href={p.website} target="_blank" rel="noreferrer">{p.website}</a></div>}
            {p.address && <div className="flex items-center gap-2"><MapPin size={15} className="text-teal-700" /> {p.address}{p.parish ? `, ${p.parish} Parish` : ""}</div>}
          </div>
        </div>
      </div>

      {/* Insurance */}
      <div className="mt-4 rounded-xl border border-slate-200 bg-white p-5">
        <h3 className="font-display text-lg font-bold text-slate-900">Accepted insurance</h3>
        <p className="text-xs text-slate-500">Verify coverage directly with the provider.</p>
        <div className="mt-3 flex flex-wrap gap-2">
          {openPayers.length === 0 && contractedOnly.length === 0 && <span className="text-sm text-slate-500">No payer information reported.</span>}
          {openPayers.map((x) => (
            <span key={x.payer} className="rounded-md border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">
              {x.payer} · accepting
            </span>
          ))}
          {contractedOnly.map((x) => (
            <span key={x.payer} className="rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-500">
              {x.payer} · contracted, not currently accepting
            </span>
          ))}
        </div>
      </div>

      {/* Transportation */}
      <div className="mt-4 rounded-xl border border-slate-200 bg-white p-5">
        <h3 className="flex items-center gap-2 font-display text-lg font-bold text-slate-900"><Bus size={18} className="text-teal-700" /> Transportation</h3>
        <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
          <Field label="Status">{(p.transportation?.status || "unavailable").replace(/_/g, " ")}</Field>
          <Field label="Service area">{p.transportation?.service_area}</Field>
          <Field label="Hours">{p.transportation?.hours}</Field>
          <Field label="Required lead time">{p.transportation?.lead_time}</Field>
          <Field label="Cost to patient">{p.transportation?.cost}</Field>
          <Field label="Wheelchair accessible">{p.transportation?.wheelchair ? "Yes" : "—"}</Field>
        </div>
      </div>

      {/* Additional services */}
      {p.additional_services?.length > 0 && (
        <div className="mt-4 rounded-xl border border-slate-200 bg-white p-5">
          <h3 className="font-display text-lg font-bold text-slate-900">Approved additional services</h3>
          <div className="mt-3 flex flex-wrap gap-2">
            {p.additional_services.map((s, i) => (
              <span key={i} className="rounded-md border border-teal-200 bg-teal-50 px-2.5 py-1 text-xs font-semibold text-teal-800 capitalize">
                {(s.type || "").replace(/_/g, " ")}
              </span>
            ))}
          </div>
          {p.description && <p className="mt-3 text-sm text-slate-600">{p.description}</p>}
        </div>
      )}

      {/* Disclaimer */}
      <div className="my-6 rounded-xl border-2 border-slate-300 bg-slate-100 p-5 text-sm font-medium text-slate-700" data-testid="disclaimer">
        <strong className="mb-1 block uppercase tracking-wide text-slate-800">Important</strong>
        {DISCLAIMER}
      </div>
    </div>
  );
}
